import type {
  WorkingStateFact,
  WorkingStateProposal,
  WorkingStateValue,
  WorkingStateValueType,
  WorkingStateWriteInput,
} from "@/api/admin-api"

/**
 * Working-state panel helpers (#3023). Non-component exports live here so the
 * panel file satisfies `react-refresh/only-export-components`.
 */

/** Human-readable rendering of a typed value plus its optional unit. */
export function formatWorkingStateValue(
  entry: Pick<WorkingStateValue | WorkingStateProposal, "value" | "value_type" | "unit">,
  locale: string | undefined
): string {
  const { value, value_type: valueType, unit } = entry
  let text: string
  if (valueType === "number" && typeof value === "number") {
    text = new Intl.NumberFormat(locale).format(value)
  } else if (valueType === "boolean") {
    text = value ? "true" : "false"
  } else if (valueType === "json") {
    text = JSON.stringify(value)
  } else {
    text = String(value ?? "")
  }
  return unit ? `${text} ${unit}` : text
}

/** The correction editor's draft: everything is edited as text. */
export type WorkingStateCorrectionDraft = {
  value: string
  valueType: WorkingStateValueType
  unit: string
  source: string
}

/** Seed the correction editor from the fact's current active value. */
export function correctionDraftFromFact(fact: WorkingStateFact): WorkingStateCorrectionDraft {
  const active = fact.active
  let value = ""
  if (active != null) {
    value = active.value_type === "json" ? JSON.stringify(active.value) : String(active.value ?? "")
  }
  return {
    value,
    valueType: active?.value_type ?? "number",
    unit: active?.unit ?? "",
    source: "",
  }
}

/**
 * Parse the drafted text back into a typed payload for PUT. Returns an error
 * key (under adminDashboard.hostedAgents.workingState.errors.*) when the text
 * does not parse as the chosen type — the backend would refuse it anyway.
 */
export function correctionInputFromDraft(
  draft: WorkingStateCorrectionDraft
): { input: WorkingStateWriteInput } | { errorKey: string } {
  const text = draft.value.trim()
  if (!text) return { errorKey: "valueRequired" }
  if (!draft.source.trim()) return { errorKey: "sourceRequired" }
  let value: unknown
  if (draft.valueType === "number") {
    const parsed = Number(text.replace(",", "."))
    if (!Number.isFinite(parsed)) return { errorKey: "valueNotNumber" }
    value = parsed
  } else if (draft.valueType === "boolean") {
    if (text !== "true" && text !== "false") return { errorKey: "valueNotBoolean" }
    value = text === "true"
  } else if (draft.valueType === "json") {
    try {
      value = JSON.parse(text)
    } catch {
      return { errorKey: "valueNotJson" }
    }
  } else {
    value = text
  }
  return {
    input: {
      value,
      value_type: draft.valueType,
      unit: draft.unit.trim() || null,
      source: draft.source.trim(),
    },
  }
}
